import { ReactNode, isValidElement } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCheck, CheckCircle2, FileQuestion } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Sparkline } from "@/components/ui/sparkline";
import { cn } from "@/lib/utils";

export type EmptyStateButtonAction = {
  label: string;
  onClick: () => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  icon?: ReactNode;
};

export type EmptyStateAction = ReactNode | EmptyStateButtonAction;

export type EmptyStateIcon = React.ElementType | ReactNode;

export type EmptyStateStep =
  | string
  | {
      title: string;
      description?: string;
      /** When set, the step title renders as a router link to this path. */
      href?: string;
      /** Short time estimate shown next to the step title, e.g. "~2 min". */
      meta?: string;
      /** Renders the step as completed (success check instead of its number). */
      done?: boolean;
    };

export type EmptyStateStat = {
  value: string;
  label: string;
  tone?: "success" | "neutral";
  /** Optional inline sparkline (needs >= 2 points). */
  trend?: number[];
};

export interface EmptyStateProps {
  title: string;
  description?: string;
  /** Supported for older callsites. */
  message?: string;
  icon?: EmptyStateIcon;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  steps?: EmptyStateStep[];
  /** Supported for older callsites. */
  action?: EmptyStateAction;
  /**
   * "empty" (default): standard empty state with dashed border.
   * "clear-queue": calm acknowledgment for completed work queues.
   * "celebrate": cleared-queue moment with a momentum stat and next-best actions.
   * "teach": first-run setup — numbered steps beside a payoff preview.
   * "confirm": calm confirmation — a summary of stats instead of a blank box.
   */
  variant?: "empty" | "clear-queue" | "celebrate" | "teach" | "confirm";
  /** Optional summary for clear-queue variant (e.g., "12 batches processed this week") */
  completedSummary?: string;
  /** celebrate: overrides the default headline (falls back to `title`). */
  celebrateHeadline?: string;
  /** celebrate: a momentum stat, e.g. "6-day clearing streak". Omit if not truthfully available. */
  streak?: string;
  /** teach: the payoff-preview panel rendered in the right-hand column. */
  preview?: ReactNode;
  /** confirm: summary stat tiles rendered below the headline. */
  stats?: EmptyStateStat[];
  className?: string;
  /**
   * Icon sizing override for "empty"/"clear-queue". For "teach"/"confirm" this
   * instead sets the module-tinted icon tile's background/text classes
   * (e.g. "bg-[#e8f5ee] text-[#1a7a4c]").
   */
  iconClassName?: string;
  children?: ReactNode;
}

export function EmptyState({
  title,
  description,
  message,
  icon,
  primaryAction,
  secondaryAction,
  steps,
  action,
  variant = "empty",
  completedSummary,
  celebrateHeadline,
  streak,
  preview,
  stats,
  className,
  iconClassName,
  children,
}: EmptyStateProps) {
  const text = message ?? description;
  const isClearQueue = variant === "clear-queue";
  const DefaultIcon = isClearQueue ? CheckCircle2 : FileQuestion;
  const resolvedPrimaryAction = primaryAction ?? action;

  // `icon` can be a rendered element (<Foo />), a plain function component,
  // a forwardRef/memo-wrapped component (e.g. every lucide-react icon — its
  // runtime `typeof` is "object", not "function", since it's created via
  // React.forwardRef), or — since EmptyStateIcon widens to ReactNode — a
  // bare string/number/boolean (some call sites pass icon="inbox" as a
  // semantic tag, not a component). Only the first two are ever rendered as
  // `<Icon />`; primitives fall back to the default icon exactly like
  // before, instead of being rendered as a bogus intrinsic tag (`<inbox />`).
  const resolveIcon = (
    value: EmptyStateIcon | undefined,
    fallback: React.ElementType
  ): { Icon: React.ElementType; node: ReactNode | null } => {
    if (!value) return { Icon: fallback, node: null };
    if (isValidElement(value)) return { Icon: fallback, node: value };
    if (typeof value !== "function" && typeof value !== "object") {
      return { Icon: fallback, node: null };
    }
    return { Icon: value as React.ElementType, node: null };
  };

  const { Icon, node: iconNode } = resolveIcon(icon, DefaultIcon);

  const isButtonAction = (value: EmptyStateAction): value is EmptyStateButtonAction => {
    return (
      !!value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !isValidElement(value) &&
      "label" in value &&
      "onClick" in value
    );
  };

  const renderAction = (
    value: EmptyStateAction,
    fallbackVariant?: ButtonProps["variant"],
    isPrimary?: boolean,
    extraClassName?: string
  ) => {
    if (!value) return null;
    if (isButtonAction(value)) {
      return (
        <Button
          onClick={value.onClick}
          variant={value.variant ?? fallbackVariant}
          size={value.size ?? "default"}
          className={cn(
            "w-full sm:w-auto",
            value.icon && "gap-2",
            isPrimary && "shadow-soft hover:shadow-soft-md transition-shadow duration-200",
            // Only apply the variant-specific treatment when the caller
            // didn't pass its own explicit button variant.
            !value.variant && extraClassName
          )}
        >
          {value.icon}
          {value.label}
        </Button>
      );
    }
    return value;
  };

  if (variant === "celebrate") {
    const { Icon: CelebrateIcon, node: celebrateIconNode } = resolveIcon(icon, Check);
    return (
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl border px-6 py-[26px] text-left animate-fade-in sm:px-7 sm:py-[30px]",
          "border-[hsl(var(--success-200))] bg-gradient-to-br from-card to-[hsl(var(--success-50))]",
          className
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_top_right,hsl(var(--success-50))_0%,transparent_60%)]"
          aria-hidden="true"
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--success-100))] animate-fg-float">
            <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-forge-success shadow-[0_8px_18px_-6px_rgba(45,157,94,0.55)]">
              {celebrateIconNode ?? (
                <CelebrateIcon className="h-[26px] w-[26px] text-white" strokeWidth={2.4} />
              )}
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-display text-[23px] leading-tight text-foreground">
              {celebrateHeadline ?? title}
            </h3>
            {text && <p className="text-[13.5px] leading-relaxed text-muted-foreground">{text}</p>}
            {streak && (
              <p className="text-[13.5px] font-semibold text-forge-success">{streak}</p>
            )}
            {(resolvedPrimaryAction || secondaryAction) && (
              <div className="flex flex-col gap-2 pt-2 sm:flex-row">
                {resolvedPrimaryAction &&
                  renderAction(
                    resolvedPrimaryAction,
                    "outline",
                    true,
                    "border-forge-success/40 text-forge-success hover:bg-[hsl(var(--success-50))]"
                  )}
                {secondaryAction && renderAction(secondaryAction, "outline", false)}
              </div>
            )}
          </div>
        </div>
        {children}
      </div>
    );
  }

  if (variant === "teach") {
    const { Icon: TeachIcon, node: teachIconNode } = resolveIcon(icon, FileQuestion);
    return (
      <div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card animate-fade-in sm:flex-row",
          className
        )}
      >
        <div className="flex flex-1 flex-col gap-4 px-6 py-6 text-left">
          <div
            className={cn(
              "flex h-[42px] w-[42px] items-center justify-center rounded-[11px]",
              iconClassName ?? "bg-primary-tint text-primary"
            )}
          >
            {teachIconNode ?? <TeachIcon className="h-6 w-6" />}
          </div>
          <div className="space-y-1.5">
            <h3 className="text-[17px] font-semibold text-foreground">{title}</h3>
            {text && <p className="text-[13px] leading-[1.55] text-muted-foreground">{text}</p>}
          </div>
          {steps && steps.length > 0 && (
            <ol className="space-y-2.5">
              {(() => {
                const firstIncompleteIndex = steps.findIndex(
                  (step) => !(typeof step === "string" ? false : Boolean(step.done))
                );
                return steps.map((step, index) => {
                const stepTitle = typeof step === "string" ? step : step.title;
                const stepDescription = typeof step === "string" ? undefined : step.description;
                const stepHref = typeof step === "string" ? undefined : step.href;
                const stepMeta = typeof step === "string" ? undefined : step.meta;
                const stepDone = typeof step === "string" ? false : Boolean(step.done);
                // Highlight the next actionable step, not always the first —
                // a checklist with earlier steps already done should still
                // point at what's next, not gray out everything after row 1.
                const isActive =
                  !stepDone && index === (firstIncompleteIndex === -1 ? 0 : firstIncompleteIndex);
                return (
                  <li key={`${stepTitle}-${index}`} className="flex items-start gap-3">
                    <span
                      className={cn(
                        "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                        stepDone
                          ? "bg-forge-success text-white"
                          : isActive
                            ? "bg-forge-success text-white"
                            : "bg-muted text-muted-foreground"
                      )}
                    >
                      {stepDone ? <Check className="h-3.5 w-3.5" /> : index + 1}
                    </span>
                    <div className="space-y-0.5">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        {stepHref ? (
                          <Link
                            to={stepHref}
                            className={cn(
                              "text-meta underline-offset-2 hover:underline",
                              isActive || stepDone
                                ? "font-semibold text-foreground"
                                : "font-medium text-muted-foreground"
                            )}
                          >
                            {stepTitle}
                          </Link>
                        ) : (
                          <p
                            className={cn(
                              "text-meta",
                              isActive || stepDone
                                ? "font-semibold text-foreground"
                                : "text-muted-foreground"
                            )}
                          >
                            {stepTitle}
                          </p>
                        )}
                        {stepMeta && (
                          <span className="text-[13px] font-medium text-muted-foreground">{stepMeta}</span>
                        )}
                      </div>
                      {stepDescription && (
                        <p className="text-meta text-muted-foreground">{stepDescription}</p>
                      )}
                    </div>
                  </li>
                );
                });
              })()}
            </ol>
          )}
          {(resolvedPrimaryAction || secondaryAction) && (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              {resolvedPrimaryAction && renderAction(resolvedPrimaryAction, undefined, true)}
              {secondaryAction && renderAction(secondaryAction, "outline", false)}
            </div>
          )}
        </div>
        {preview && (
          <div className="relative w-full shrink-0 border-t border-border bg-gradient-to-b from-muted/40 to-background px-5 py-5 sm:w-[200px] sm:border-l sm:border-t-0">
            <span className="absolute right-3 top-3 rounded-[5px] border border-border bg-card px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
              Preview
            </span>
            <div className="mt-6">{preview}</div>
          </div>
        )}
        {children}
      </div>
    );
  }

  if (variant === "confirm") {
    const { Icon: ConfirmIcon, node: confirmIconNode } = resolveIcon(icon, CheckCheck);
    return (
      <div
        className={cn(
          "relative flex flex-col gap-4 rounded-2xl border border-border bg-card px-6 py-[22px] text-left animate-fade-in",
          className
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px]",
              iconClassName ?? "bg-primary-tint text-primary"
            )}
          >
            {confirmIconNode ?? <ConfirmIcon className="h-5 w-5" />}
          </div>
          <div className="space-y-1">
            <h3 className="text-[17px] font-semibold text-foreground">{title}</h3>
            {text && <p className="text-sm text-muted-foreground">{text}</p>}
          </div>
        </div>
        {stats && stats.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={cn(
                  "flex min-w-[140px] flex-1 items-center justify-between gap-3 rounded-xl border px-4 py-3",
                  stat.tone === "success"
                    ? "border-[hsl(var(--success-200))] bg-[hsl(var(--success-50))]"
                    : "border-border bg-muted/30"
                )}
              >
                <div>
                  <p
                    className={cn(
                      "text-xl font-bold tabular-nums",
                      stat.tone === "success" ? "text-forge-success" : "text-foreground"
                    )}
                  >
                    {stat.value}
                  </p>
                  <p
                    className={cn(
                      "text-[11.5px]",
                      stat.tone === "success" ? "text-forge-success/80" : "text-muted-foreground"
                    )}
                  >
                    {stat.label}
                  </p>
                </div>
                {stat.trend && stat.trend.length >= 2 && (
                  <Sparkline
                    data={stat.trend}
                    width={48}
                    height={20}
                    color={stat.tone === "success" ? "hsl(var(--success))" : "hsl(var(--muted-foreground))"}
                    fill={false}
                    showEndDot={false}
                    className="h-5 w-12 shrink-0"
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {(resolvedPrimaryAction || secondaryAction) && (
          <div className="flex flex-col gap-2 sm:flex-row">
            {resolvedPrimaryAction &&
              renderAction(
                resolvedPrimaryAction,
                // Base on `secondary` rather than the default `primary` variant:
                // the primary's `hover:bg-primary-hover` outlives a bg-color
                // utility added here, so the button would turn gold on hover
                // instead of staying dark.
                "secondary",
                true,
                "bg-foreground text-background hover:bg-foreground/90"
              )}
            {secondaryAction && renderAction(secondaryAction, "outline", false)}
          </div>
        )}
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex flex-col items-center gap-4 rounded-2xl px-6 py-8 text-center sm:gap-5 sm:py-12",
        "animate-fade-in",
        isClearQueue
          ? "border border-success-200/60 bg-success-50/20 shadow-soft"
          : "border border-border/40 bg-muted/20 shadow-soft",
        className
      )}
    >
      {/* Subtle radial gradient background for visual focus */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 rounded-2xl bg-gradient-radial from-transparent",
          isClearQueue ? "to-success-50/30" : "to-primary-tint"
        )}
        aria-hidden="true"
      />

      {/* Icon container */}
      <div
        className={cn(
          "relative flex h-16 w-16 items-center justify-center sm:h-20 sm:w-20",
          "animate-pulse-subtle drop-shadow-md",
          isClearQueue
            ? "text-success"
            : "text-primary"
        )}
      >
        {iconNode ?? (
          <Icon className={cn("h-12 w-12 sm:h-16 sm:w-16", iconClassName)} />
        )}
      </div>

      {/* Text content */}
      <div className="relative space-y-1.5">
        <h3 className="text-section-title font-medium tracking-tight text-foreground">
          {title}
        </h3>
        {text && (
          <p className="text-sm leading-relaxed text-muted-foreground/80">
            {text}
          </p>
        )}
        {isClearQueue && completedSummary && (
          <p className="text-meta font-medium text-success-600">
            {completedSummary}
          </p>
        )}
      </div>

      {/* Action buttons */}
      {(resolvedPrimaryAction || secondaryAction) && (
        <div
          className={cn(
            "relative flex w-full flex-col items-stretch gap-2 pt-1 sm:w-auto sm:flex-row sm:justify-center",
            steps?.length ? "order-3 sm:order-4" : undefined
          )}
        >
          {resolvedPrimaryAction && renderAction(resolvedPrimaryAction, undefined, true)}
          {secondaryAction && renderAction(secondaryAction, "outline", false)}
        </div>
      )}

      {/* Steps section */}
      {steps && steps.length > 0 && (
        <div className="relative order-4 w-full max-w-md rounded-xl border border-border/60 bg-background/70 px-4 py-3 text-left sm:order-3">
          <p className="text-meta font-semibold text-muted-foreground">
            Suggested steps
          </p>
          <ol className="mt-3 space-y-2 text-meta text-muted-foreground">
            {steps.map((step, index) => {
              const stepTitle = typeof step === "string" ? step : step.title;
              const stepDescription = typeof step === "string" ? undefined : step.description;
              const stepHref = typeof step === "string" ? undefined : step.href;
              return (
                <li key={`${stepTitle}-${index}`} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-muted text-meta font-semibold text-foreground">
                    {index + 1}
                  </span>
                  <div className="space-y-0.5">
                    {stepHref ? (
                      <Link
                        to={stepHref}
                        className="text-meta font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {stepTitle}
                      </Link>
                    ) : (
                      <p className="text-meta font-medium text-foreground">{stepTitle}</p>
                    )}
                    {stepDescription && (
                      <p className="text-meta text-muted-foreground">{stepDescription}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
      {children}
    </div>
  );
}
